import { useTranslation } from "@/hooks/useTranslation";
import { useCreateSingleSender } from "@/services";
import { Card, CardContent, toast } from "@st-marketplace/ui";
import { useCallback, useState } from "react";
import NewFormSenderForm, { type NewFormSenderFormData } from "./Form";

const INITIAL_FORM_DATA: NewFormSenderFormData = {
	reserved_balance: null,
	use_inherited_keep: true,
	use_inherited_priority: true,
	proxy: "",
	email: "",
	password: "",
	auth_type: "gauth",
	gauth: "",
	backup_code: [],
	imap_server: "",
	imap_port: null,
	imap_username: "",
	imap_password: "",
	imap_ssl: false,
	sender_group_name: "",
	priority: null,
};

const NewFormSender: React.FC = () => {
	const { t } = useTranslation();
	const createSenderMutation = useCreateSingleSender();

	const [formData, setFormData] =
		useState<NewFormSenderFormData>(INITIAL_FORM_DATA);

	const handleReset = () => {
		setFormData(INITIAL_FORM_DATA);
	};

	const handleFormChange = useCallback(
		(name: keyof NewFormSenderFormData, value: string) => {
			setFormData((prevData) => ({
				...prevData,
				[name]: value,
			}));
		},
		[],
	);

	const handleSubmit = (data: NewFormSenderFormData) => {
		if (!data.gauth && !data.backup_code?.length && !data.imap_server) {
			toast.error(t("addSender.gauthOrBackupCodeOrIMAPRequired"));
			return;
		}
		if (data.imap_server && !data.imap_port) {
			toast.error(t("addSender.imapPortRequired"));
			return;
		}
		createSenderMutation.mutate(
			{
				body: {
					to_keep: data.reserved_balance,
					platform: "ps",
					ea_email: data.email,
					ea_password: data.password,
					auth:
						data.auth_type === "gauth"
							? { type: "gauth", auth: data.gauth }
							: data.auth_type === "backupCode"
								? { type: "backup_code", auth: data.backup_code }
								: {
										type: "imap",
										username: data.imap_username,
										password: data.imap_password,
										server: data.imap_server,
										port: data.imap_port!,
										ssl: data.imap_ssl,
									},
					proxy_id: data.proxy,
					sender_group_id: data.sender_group_name,
					priority_usage: data.use_inherited_priority
						? null
						: data.priority ?? null,
				},
			},
			{
				onSuccess: () => {
					toast.success(t("addSender.successMessage"));
					handleReset();
				},
			},
		);
	};

	return (
		<Card className="mb-10">
			<CardContent className="px-[5%] py-[3%]">
				<NewFormSenderForm
					formData={formData}
					loading={createSenderMutation.isPending}
					onFormChange={handleFormChange}
					onSubmit={handleSubmit}
					onReset={handleReset}
				/>
			</CardContent>
		</Card>
	);
};

export default NewFormSender;
